import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	HostBinding,
	OnDestroy,
	OnInit,
	Output,
	ViewEncapsulation,
} from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { select, Store } from "@ngrx/store";
import {
	CookieConsentSelector,
	MARKETING_SETTINGS_MESSAGE_CODE,
	MARKETING_SETTINGS_MESSAGE_CONTEXT,
	MarketingSettingsAction,
} from "@obg/ngx.common";
import {
	AbstractTranslatableSnackBarService,
	AuthSelector,
	CoreState,
	MessageService,
	MessageType,
} from "@obg/ngx.core";
import { TranslationGroup } from "@obg/ngx.core.content";
import { RouteAction } from "@obg/ngx.router";
import { merge, Observable, Subject, Subscription } from "rxjs";
import { concatAll, filter, take, tap } from "rxjs/operators";

@Component({
	selector: "obg-m-cookie-settings-container",
	templateUrl: "./cookie-settings.container.html",
	styleUrls: ["./cookie-settings.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class CookieSettingsContainer implements OnInit, OnDestroy {
	// TODO [OneTrust]:
	// This file needs to be refactored once oneTrust is rolled out on all the brands: https://edgealpha.corpsson.com/browse/AOCC-3162
	@Output() submitClick = new EventEmitter();
	@HostBinding("class.obg-m-cookie-settings") hostCssClass = true;

	formGroup: UntypedFormGroup;
	isLoading = false;
	isAuth = false;
	isChanged = false;

	private data$$: Subscription | undefined;
	private cookieConsentAccepted$: Observable<boolean> | undefined;
	private isCookieConsentAccepted = false;
	private submit$ = new Subject<void>();

	constructor(
		private formBuilder: UntypedFormBuilder,
		private store: Store<CoreState>,
		private selector: CookieConsentSelector,
		private changeDetector: ChangeDetectorRef,
		private messageService: MessageService,
		private snackBarService: AbstractTranslatableSnackBarService,
		private marketingSettingsAction: MarketingSettingsAction
	) {
		this.formGroup = this.formBuilder.group({
			cookieConsent: false,
		});
	}

	openSnackBar(type: MessageType) {
		this.snackBarService
			.openSnackBar$(
				{
					message:
						type === MessageType.Success
							? `marketing-settings.consents.${MARKETING_SETTINGS_MESSAGE_CODE.updateCustomerConsentsSuccess}`
							: "general.generic-error",
					action: "general.close",
					duration: 4000,
					type,
				},
				[TranslationGroup.Common]
			)
			.pipe(take(1))
			// eslint-disable-next-line rxjs/no-ignored-subscription
			.subscribe();
	}

	ngOnInit() {
		this.cookieConsentAccepted$ = this.store.pipe(
			select(this.selector.isAccepted()),
			tap((cookieValue) => {
				this.isLoading = false;
				this.isCookieConsentAccepted = cookieValue;
				this.isChanged = false;
				this.formGroup.patchValue({ cookieConsent: cookieValue });
			})
		);

		const cookieSettingsMessages$ = this.messageService
			.consume$({
				context: MARKETING_SETTINGS_MESSAGE_CONTEXT.consents,
			})
			.pipe(
				filter(() => this.isAuth),
				concatAll(),
				tap((message) => {
					this.isLoading = false;
					this.openSnackBar(message.type);
				})
			);

		const isAuth$ = this.store.select(AuthSelector.isAuthenticated).pipe(tap((isAuth) => (this.isAuth = isAuth)));

		const onSubmit$ = this.submit$.pipe(
			tap(() => {
				this.isLoading = true;

				if (this.isChanged && !this.isAuth) {
					this.isLoading = false;
					this.openSnackBar(MessageType.Success);
				}

				this.store.dispatch(
					this.marketingSettingsAction.updateConsents({ cookie: this.formGroup.controls.cookieConsent.value })
				);
				this.submitClick.emit();
				this.store.dispatch(RouteAction.back());
			})
		);

		this.data$$ = merge(
			isAuth$,
			cookieSettingsMessages$,
			this.cookieConsentAccepted$,
			this.formGroup.valueChanges.pipe(
				tap(
					(value: { cookieConsent: boolean }) =>
						(this.isChanged = this.isCookieConsentAccepted !== value.cookieConsent)
				)
			),
			onSubmit$
		).subscribe(() => this.changeDetector.markForCheck());
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
	}

	onSubmit() {
		this.submit$.next();
	}

	resetForm() {
		this.formGroup.reset({
			cookieConsent: this.isCookieConsentAccepted,
		});
	}
}
